// Control UI module implements theme behavior.
//
// User-facing family names were renamed to the Altto cosmic set (2026-07-19,
// AV2 R4): claw->nova (default), knot->eclipse, dash->orbit. The internal
// ResolvedTheme / data-theme attribute values ("dark", "openknot",
// "openknot-light", "dash", "dash-light", ...) were deliberately KEPT as-is —
// renaming those would also require touching every base.css selector and
// every consumer that reads document.documentElement.dataset.theme, for zero
// user-visible benefit (nothing renders that string). Only the stored/typed
// family identifier is renamed. LEGACY_THEME_NAMES below keeps old stored
// settings (claw/knot/dash) resolving correctly after the rename.
export type ThemeName = "nova" | "eclipse" | "orbit" | "custom";
export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme =
  | "dark"
  | "light"
  | "openknot"
  | "openknot-light"
  | "dash"
  | "dash-light"
  | "custom"
  | "custom-light";

const VALID_THEME_NAMES = new Set<ThemeName>(["nova", "eclipse", "orbit", "custom"]);
const VALID_THEME_MODES = new Set<ThemeMode>(["system", "light", "dark"]);

/** Pre-rename family names, still found in users' stored settings. */
const LEGACY_THEME_NAMES: Record<string, ThemeName> = {
  claw: "nova",
  knot: "eclipse",
  dash: "orbit",
};

function prefersLightScheme(): boolean {
  if (typeof globalThis.matchMedia !== "function") {
    return false;
  }
  return globalThis.matchMedia("(prefers-color-scheme: light)").matches;
}

export function parseThemeSelection(
  themeRaw: unknown,
  modeRaw: unknown,
): { theme: ThemeName; mode: ThemeMode } {
  const themeRawStr = typeof themeRaw === "string" ? themeRaw : "";
  const mode = typeof modeRaw === "string" ? modeRaw : "";
  const theme = LEGACY_THEME_NAMES[themeRawStr] ?? themeRawStr;

  const normalizedTheme = VALID_THEME_NAMES.has(theme as ThemeName) ? (theme as ThemeName) : "nova";
  const normalizedMode = VALID_THEME_MODES.has(mode as ThemeMode) ? (mode as ThemeMode) : "system";

  return { theme: normalizedTheme, mode: normalizedMode };
}

function resolveMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") {
    return prefersLightScheme() ? "light" : "dark";
  }
  return mode;
}

export function resolveTheme(theme: ThemeName, mode: ThemeMode): ResolvedTheme {
  const resolvedMode = resolveMode(mode);
  if (theme === "nova") {
    return resolvedMode === "light" ? "light" : "dark";
  }
  if (theme === "eclipse") {
    return resolvedMode === "light" ? "openknot-light" : "openknot";
  }
  if (theme === "orbit") {
    return resolvedMode === "light" ? "dash-light" : "dash";
  }
  return resolvedMode === "light" ? "custom-light" : "custom";
}
